import { ReactElement, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card } from '@mui/material';
import { useAwsEffect, useAwsGetter, useAwsKeysMemo, useRoutes } from '@localstack/services';
import { LAMBDA_DEFAULT_PAGE_SIZE } from '@localstack/constants';
import { ButtonWithDialog, Dropdown, LambdaWebEndpointsTable, PageTitle } from '@localstack/ui';

import { LambdaProps } from './types';
import { LambdaWebVersionGate } from './components/LambdaWebVersionGate';
import { WebFunctionTabs } from './components/WebFunctionTabs';

export const LambdaWebEndpoints = (props: LambdaProps): ReactElement => (
  <LambdaWebVersionGate {...props}>
    <LambdaWebEndpointsContent {...props} />
  </LambdaWebVersionGate>
);

const LambdaWebEndpointsContent = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { name = '' } = useParams<'name'>();
  const { goto } = useRoutes();
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [paginationKey, setPaginationKey] = useState<string>();
  const { data, isLoading, mutate } = useAwsGetter(
    'LambdaWeb',
    'listWebFunctionEndpoints',
    [{ functionName: name, maxResults: LAMBDA_DEFAULT_PAGE_SIZE, nextToken: paginationKey }],
    { clientOverrides },
  );
  const paginationKeys = useAwsKeysMemo(page, data?.nextToken);
  useEffect(() => {
    setPaginationKey(paginationKeys[page]);
    setSelected([]);
  }, [page]);
  const { deleteWebFunctionEndpoint, isLoading: deleting } = useAwsEffect('LambdaWeb', ['deleteWebFunctionEndpoint'], {
    clientOverrides,
    revalidate: ['listWebFunctionEndpoints'],
  });
  const handleDelete = async () => {
    await Promise.all(selected.map((endpointName) => deleteWebFunctionEndpoint({ functionName: name, endpointName })));
    setSelected([]);
  };
  return (
    <Layout
      documentTitle="Lambda: Web Function Endpoints"
      tabs={<WebFunctionTabs routes={routes} functionName={name} clientOverrides={clientOverrides} />}
      title={
        <PageTitle
          title="Endpoints"
          onMutate={mutate}
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Web Functions', () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTIONS)],
            [name, () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTION, { name })],
            ['Endpoints', null],
          ]}
        />
      }
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_LAMBDA_WEB_ENDPOINT_NEW, { name })}>
            Create
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={!selected.length || deleting}
              dialogTitle={`Delete ${selected.length} Endpoint(s)?`}
              dialogContent="Selected endpoints will be permanently deleted"
              onClick={handleDelete}
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <LambdaWebEndpointsTable
          endpoints={data?.endpoints ?? []}
          page={page}
          onPageChange={setPage}
          hasMore={!!data?.nextToken}
          loading={isLoading}
          onSelect={setSelected}
          onViewEndpoint={(endpointName) => goto(routes.RESOURCES_LAMBDA_WEB_ENDPOINT, { name, endpointName })}
        />
      </Card>
    </Layout>
  );
};
